import { forwardRef, lazy, memo, Suspense } from 'react';
import type { CKEditor } from '@ckeditor/ckeditor5-react';
import type { InlineEditor as CKInlineEditor } from 'ckeditor5';

import type { InlineEditorProps } from './InlineEditor.types';
import { loadBaseInlineEditor } from './BaseInlineEditor.lazy';

export * from './InlineEditor.types';

const LazyInlineEditor = lazy(() => loadBaseInlineEditor().then((module) => ({ default: module.BaseInlineEditor })));

export const preloadInlineEditor = loadBaseInlineEditor;

export type InlineEditorInstance = CKEditor<CKInlineEditor>;

const InlineEditorBase = (
  { loadingFallback, ...props }: InlineEditorProps,
  ref: React.ForwardedRef<InlineEditorInstance>,
) => {
  // Reserve the editor's min height while the chunk loads so surrounding layout doesn't collapse
  // and jump once CKEditor mounts (22px per line mirrors --inline-editor-min-lines; 24px is the
  // editable area's base min-height).
  const fallbackMinHeight = Math.max((props.minLines ?? 0) * 22, 24);

  return (
    <Suspense fallback={loadingFallback ?? <div style={{ minHeight: fallbackMinHeight }} />}>
      <LazyInlineEditor {...props} ref={ref} />
    </Suspense>
  );
};

export const InlineEditor = memo(forwardRef(InlineEditorBase));
